import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { Calculator, Gauge, LineChart } from "lucide-react";

const COUNTRIES = [
  { v: "germany", n: "Germany" },
  { v: "netherlands", n: "Netherlands" },
  { v: "romania", n: "Romania" },
  { v: "poland", n: "Poland" },
  { v: "bulgaria", n: "Bulgaria" },
  { v: "remote-europe", n: "Remote Europe" },
];

const SENIORITY = [
  { v: "junior", n: "Junior" },
  { v: "mid", n: "Mid" },
  { v: "senior", n: "Senior" },
  { v: "lead", n: "Lead / Principal" },
];

const ROLES = [
  "Software Engineer", "SAP Consultant", "AI Engineer",
  "Data Engineer", "DevOps Engineer", "Product Manager", "Executive",
];

function fmtEUR(n) {
  return new Intl.NumberFormat("en-EU", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}

export default function LeadMagnets() {
  return (
    <section
      id="tools"
      data-testid="lead-magnets-section"
      className="py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
            Free tools for hiring managers
          </div>
          <h2 className="font-display font-bold mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Plan your next hire in 60 seconds.
          </h2>
          <p className="mt-4 text-slate-600">
            Live calculators powered by our 2025 European IT salary dataset and
            placement benchmarks across 6 markets.
          </p>
        </div>

        <Tabs defaultValue="cost" className="mt-10">
          <TabsList data-testid="tools-tabs" className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
            <TabsTrigger value="cost" data-testid="tab-cost" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Calculator className="w-4 h-4 mr-1.5" /> Hiring Cost
            </TabsTrigger>
            <TabsTrigger value="salary" data-testid="tab-salary" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <LineChart className="w-4 h-4 mr-1.5" /> Salary Benchmark
            </TabsTrigger>
            <TabsTrigger value="time" data-testid="tab-time" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Gauge className="w-4 h-4 mr-1.5" /> Time-to-Hire
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cost">
            <HiringCostCalc />
          </TabsContent>
          <TabsContent value="salary">
            <SalaryBenchmarkCalc />
          </TabsContent>
          <TabsContent value="time">
            <TimeToHireCalc />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

function CalcShell({ children, result }) {
  return (
    <div className="mt-6 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        {children}
      </div>
      <div className="lg:col-span-5 rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 sm:p-8">
        {result}
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function HiringCostCalc() {
  const [inp, setInp] = useState({ country: "germany", role: "Software Engineer", seniority: "senior", headcount: 1 });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculators/hiring-cost", { ...inp, headcount: Number(inp.headcount) || 1 });
      setRes(data);
    } finally { setLoading(false); }
  };

  return (
    <CalcShell
      result={
        <div data-testid="hiring-cost-result">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-semibold">Estimate</div>
          <div className="mt-2 text-4xl font-display font-black">
            {res ? fmtEUR(res.estimated_total_eur) : "—"}
          </div>
          <p className="mt-2 text-slate-300 text-sm">
            Total agency-managed hiring cost for {inp.headcount}×{" "}
            {inp.seniority} {inp.role} in {inp.country}.
          </p>
          {res && (
            <div className="mt-6 space-y-3 text-sm">
              <Row k="Annual gross salary (each)" v={fmtEUR(res.annual_salary_eur)} />
              <Row k="Placement fee" v={`${res.placement_fee_pct}%`} />
              <Row k="DIY in-house alternative" v={fmtEUR(res.diy_alternative_eur)} />
              <Row k="Estimated savings vs DIY" v={fmtEUR(res.estimated_savings_eur)} highlight />
            </div>
          )}
        </div>
      }
    >
      <h3 className="font-display font-bold text-2xl text-slate-900">Hiring Cost Calculator</h3>
      <p className="text-slate-600 mt-1 text-sm">Estimate fully-loaded agency cost vs DIY.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <FieldRow label="Country">
          <Select value={inp.country} onValueChange={(v) => setInp({ ...inp, country: v })}>
            <SelectTrigger data-testid="hcc-country"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map(c => <SelectItem key={c.v} value={c.v}>{c.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Role">
          <Select value={inp.role} onValueChange={(v) => setInp({ ...inp, role: v })}>
            <SelectTrigger data-testid="hcc-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Seniority">
          <Select value={inp.seniority} onValueChange={(v) => setInp({ ...inp, seniority: v })}>
            <SelectTrigger data-testid="hcc-seniority"><SelectValue /></SelectTrigger>
            <SelectContent>{SENIORITY.map(s => <SelectItem key={s.v} value={s.v}>{s.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Headcount">
          <Input data-testid="hcc-headcount" type="number" min={1}
            value={inp.headcount} onChange={(e) => setInp({ ...inp, headcount: e.target.value })}/>
        </FieldRow>
      </div>
      <Button data-testid="hcc-submit" disabled={loading} onClick={compute}
        className="mt-6 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white">
        {loading ? "Calculating…" : "Calculate cost"}
      </Button>
    </CalcShell>
  );
}

function SalaryBenchmarkCalc() {
  const [inp, setInp] = useState({ country: "germany", role: "Software Engineer", seniority: "senior" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculators/salary-benchmark", inp);
      setRes(data);
    } finally { setLoading(false); }
  };

  return (
    <CalcShell
      result={
        <div data-testid="salary-result">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-semibold">Median (2025)</div>
          <div className="mt-2 text-4xl font-display font-black">
            {res ? fmtEUR(res.median_eur) : "—"}
          </div>
          {res && (
            <div className="mt-6 space-y-3 text-sm">
              <Row k="25th percentile" v={fmtEUR(res.p25_eur)} />
              <Row k="Median (50th)" v={fmtEUR(res.median_eur)} highlight />
              <Row k="75th percentile" v={fmtEUR(res.p75_eur)} />
              <Row k="90th percentile" v={fmtEUR(res.p90_eur)} />
            </div>
          )}
          <p className="mt-4 text-slate-400 text-xs">{res?.notes}</p>
        </div>
      }
    >
      <h3 className="font-display font-bold text-2xl text-slate-900">Salary Benchmark Tool</h3>
      <p className="text-slate-600 mt-1 text-sm">Compare gross annual salaries across European markets.</p>
      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <FieldRow label="Country">
          <Select value={inp.country} onValueChange={(v) => setInp({ ...inp, country: v })}>
            <SelectTrigger data-testid="sb-country"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map(c => <SelectItem key={c.v} value={c.v}>{c.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Role">
          <Select value={inp.role} onValueChange={(v) => setInp({ ...inp, role: v })}>
            <SelectTrigger data-testid="sb-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Seniority">
          <Select value={inp.seniority} onValueChange={(v) => setInp({ ...inp, seniority: v })}>
            <SelectTrigger data-testid="sb-seniority"><SelectValue /></SelectTrigger>
            <SelectContent>{SENIORITY.map(s => <SelectItem key={s.v} value={s.v}>{s.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
      </div>
      <Button data-testid="sb-submit" disabled={loading} onClick={compute}
        className="mt-6 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white">
        {loading ? "Loading…" : "Show benchmark"}
      </Button>
    </CalcShell>
  );
}

function TimeToHireCalc() {
  const [inp, setInp] = useState({ country: "germany", role: "Software Engineer", seniority: "senior", urgency: "standard" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculators/time-to-hire", inp);
      setRes(data);
    } finally { setLoading(false); }
  };

  return (
    <CalcShell
      result={
        <div data-testid="tth-result">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-semibold">Estimated time</div>
          <div className="mt-2 text-4xl font-display font-black">
            {res ? `${res.estimated_days} days` : "—"}
          </div>
          {res && (
            <div className="mt-6 space-y-3 text-sm">
              {res.stages.map(s => <Row key={s.name} k={s.name} v={`${s.days} days`} />)}
            </div>
          )}
        </div>
      }
    >
      <h3 className="font-display font-bold text-2xl text-slate-900">Time-to-Hire Estimator</h3>
      <p className="text-slate-600 mt-1 text-sm">From kickoff to signed offer — broken down by stage.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <FieldRow label="Country">
          <Select value={inp.country} onValueChange={(v) => setInp({ ...inp, country: v })}>
            <SelectTrigger data-testid="tth-country"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map(c => <SelectItem key={c.v} value={c.v}>{c.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Role">
          <Select value={inp.role} onValueChange={(v) => setInp({ ...inp, role: v })}>
            <SelectTrigger data-testid="tth-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Seniority">
          <Select value={inp.seniority} onValueChange={(v) => setInp({ ...inp, seniority: v })}>
            <SelectTrigger data-testid="tth-seniority"><SelectValue /></SelectTrigger>
            <SelectContent>{SENIORITY.map(s => <SelectItem key={s.v} value={s.v}>{s.n}</SelectItem>)}</SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Urgency">
          <Select value={inp.urgency} onValueChange={(v) => setInp({ ...inp, urgency: v })}>
            <SelectTrigger data-testid="tth-urgency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="urgent">Urgent (rush)</SelectItem>
              <SelectItem value="executive">Executive search</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </div>
      <Button data-testid="tth-submit" disabled={loading} onClick={compute}
        className="mt-6 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white">
        {loading ? "Estimating…" : "Estimate time"}
      </Button>
    </CalcShell>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-2">
      <span className="text-slate-300">{k}</span>
      <span className={`font-display ${highlight ? "text-blue-300 font-bold" : "text-white font-semibold"}`}>{v}</span>
    </div>
  );
}
